import { Navigate, Route, Routes } from "react-router";

import { ROUTES } from "@/constants/routes";
import { ProtectedLayout } from "@/features/auth/auth-guard";
import { GlobalAccessGate } from "@/layouts/GlobalAccessGate";
import { OrganizationGate } from "@/layouts/OrganizationGate";
import { ShadowLayout } from "@/layouts/ShadowLayout";
import { Costs } from "@/pages/Costs";
import { Login } from "@/pages/Login";
import { LookoutInstallations } from "@/pages/LookoutInstallations";
import { MapConfiguration } from "@/pages/MapConfiguration";
import { NotFound } from "@/pages/NotFound";
import { Calibration } from "@/pages/plausibility/Calibration";
import { GlobalPriors } from "@/pages/plausibility/GlobalPriors";
import { PlausibilitySettings } from "@/pages/plausibility/PlausibilitySettings";
import { Replay } from "@/pages/plausibility/Replay";
import { TenantPriors } from "@/pages/plausibility/TenantPriors";
import { UnknownLabels } from "@/pages/plausibility/UnknownLabels";
import { VerificationDetail } from "@/pages/plausibility/VerificationDetail";
import { Verifications } from "@/pages/plausibility/Verifications";
import { Reanalyses } from "@/pages/reanalysis/Reanalyses";
import { ReanalysisEvent } from "@/pages/reanalysis/ReanalysisEvent";
import { SceneProposalDetail } from "@/pages/scene-facts/SceneProposalDetail";
import { SceneProposals } from "@/pages/scene-facts/SceneProposals";

/**
 * One shell, two gates. Organization pages sit behind `OrganizationGate`
 * because they act on the selected organization; the verification,
 * scene-proposal and reanalysis pages are organization-scoped too but
 * internal-only, so they sit behind both gates. Global pages need only the
 * grant.
 */
export const AppRoutes = () => (
	<Routes>
		<Route element={<Login />} path={ROUTES.LOGIN} />

		<Route element={<ProtectedLayout />}>
			<Route element={<ShadowLayout />}>
				<Route
					element={<Navigate replace to={ROUTES.ORGANIZATION_MAP} />}
					index
				/>
				<Route
					element={<Navigate replace to={ROUTES.ORGANIZATION_MAP} />}
					path={ROUTES.ORGANIZATION}
				/>
				<Route
					element={<Navigate replace to={ROUTES.GLOBAL_UNKNOWN_LABELS} />}
					path={ROUTES.GLOBAL}
				/>

				<Route element={<OrganizationGate />}>
					<Route
						element={<MapConfiguration />}
						path={ROUTES.ORGANIZATION_MAP}
					/>
					<Route
						element={<LookoutInstallations />}
						path={ROUTES.ORGANIZATION_INSTALLATIONS}
					/>
					<Route
						element={<PlausibilitySettings />}
						path={ROUTES.ORGANIZATION_PLAUSIBILITY_SETTINGS}
					/>
					<Route element={<TenantPriors />} path={ROUTES.ORGANIZATION_PRIORS} />

					<Route element={<GlobalAccessGate />}>
						<Route
							element={<Verifications />}
							path={ROUTES.ORGANIZATION_VERIFICATIONS}
						/>
						<Route
							element={<VerificationDetail />}
							path={ROUTES.ORGANIZATION_VERIFICATION}
						/>
						<Route
							element={<SceneProposals />}
							path={ROUTES.ORGANIZATION_SCENE_PROPOSALS}
						/>
						<Route
							element={<SceneProposalDetail />}
							path={ROUTES.ORGANIZATION_SCENE_PROPOSAL}
						/>
						<Route
							element={<Reanalyses />}
							path={ROUTES.ORGANIZATION_REANALYSIS}
						/>
						<Route
							element={<ReanalysisEvent />}
							path={ROUTES.ORGANIZATION_REANALYSIS_EVENT}
						/>
						<Route element={<Replay />} path={ROUTES.ORGANIZATION_REPLAY} />
						<Route element={<Costs />} path={ROUTES.ORGANIZATION_COSTS} />
					</Route>
				</Route>

				<Route element={<GlobalAccessGate />}>
					<Route
						element={<UnknownLabels />}
						path={ROUTES.GLOBAL_UNKNOWN_LABELS}
					/>
					<Route element={<GlobalPriors />} path={ROUTES.GLOBAL_PRIORS} />
					<Route element={<Calibration />} path={ROUTES.GLOBAL_CALIBRATION} />
				</Route>
			</Route>
		</Route>

		<Route element={<NotFound />} path={ROUTES.NOT_FOUND} />
	</Routes>
);
